/* tslint:disable:max-line-length */
import React, { ChangeEventHandler, useEffect, useState } from 'react';
import classNames from 'classnames';
import './EmailInput.pcss';
import {
  EMAIL_REGEX,
  EMAIL_REGEX_DOT_REQUIRED,
} from '../../../constants/validations';
import TextInput, { TextInputProps } from '../../../components/TextInput';

export type Payload = {
  e: ChangeEventHandler<HTMLInputElement>;
  isValid: boolean;
  email: string;
};

type Props = {
  className?: string;
  onChange?: (payload: Payload) => void;
  inputProps?: Partial<TextInputProps>;
  defaultValue?: string;
  withIcon?: boolean;
  strict?: boolean;
};

const EmailIcon = () => (
  <svg width="16" height="12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14.5 0h-13A1.5 1.5 0 000 1.5v9A1.5 1.5 0 001.5 12h13a1.5 1.5 0 001.5-1.5v-9A1.5 1.5 0 0014.5 0zm-13 1h13c.275 0 .5.225.5.5v1.294c-.684.578-1.662 1.375-4.706 3.79C9.766 7.004 8.725 8.012 8 8c-.725.012-1.769-.997-2.294-1.416C2.662 4.17 1.684 3.372 1 2.794V1.5c0-.275.225-.5.5-.5zm13 10h-13a.501.501 0 01-.5-.5V4.094c.712.584 1.837 1.487 4.084 3.272C5.725 7.878 6.856 9.006 8 9c1.137.01 2.26-1.11 2.916-1.634A370.038 370.038 0 0015 4.094V10.5c0 .275-.225.5-.5.5z"
      fill="#525252"
    />
  </svg>
);

export const useEmailInput = () => {
  const [emailState, setEmailState] = useState<{
    email: string;
    isValid: boolean;
  }>({
    email: '',
    isValid: false,
  });
  const onChange = ({ email, isValid }: Payload) => {
    setEmailState({
      email,
      isValid,
    });
  };
  return {
    emailState,
    onChange,
  };
};

export const EmailInput = ({
  className,
  onChange,
  inputProps = {} as TextInputProps,
  withIcon = true,
  defaultValue,
  strict,
}: Props) => {
  const [value, setValue] = useState<string | undefined>('');
  const [isValid, setIsValid] = useState<boolean | undefined>();

  const setValueAndValidity = val => {
    setValue(val);
    const validated = (strict ? EMAIL_REGEX_DOT_REQUIRED : EMAIL_REGEX).test(
      val,
    );
    setIsValid(validated);
    return [val, validated];
  };

  const onInputChange = e => {
    const [val, validated] = setValueAndValidity(e.target.value);
    onChange &&
      onChange({
        e,
        isValid: validated,
        email: val,
      });
  };

  useEffect(() => {
    if (!value && defaultValue) {
      setValueAndValidity(defaultValue);
    }
  }, []);

  return (
    <div
      className={classNames(className, 'email-input', {
        'email-input--invalid': !isValid && value,
        'email-input--no-icon': !withIcon,
        'email-input--disabled': inputProps?.disabled,
      })}
    >
      <div className="email-input__input-container">
        <span className="email-input__icon">
          <EmailIcon />
        </span>
        <TextInput
          {...inputProps}
          className={classNames('email-input__input', inputProps.className)}
          name="email"
          type="email"
          placeholder={inputProps.placeholder || 'Email address'}
          required
          value={value}
          onChange={onInputChange}
        />
      </div>
    </div>
  );
};
